import { todayInWorkspaceTz } from "@/hooks/useWorkspaceTasks";

export type TaskUrgencyLevel =
  | "overdue"
  | "today"
  | "approaching"
  | "future"
  | "undated"
  | "completed"
  | "cancelled";

export interface TaskUrgencyInput {
  status: "pending" | "completed" | "cancelled";
  due_date: string | null;
  due_at?: string | null;
}

export interface TaskUrgencyResult {
  level: TaskUrgencyLevel;
  label: string | null;
  cardClassName: string;
  badgeVariant: "destructive" | "default" | "outline" | null;
  badgeClassName?: string;
}

function addDaysISO(dateIso: string, days: number): string {
  const d = new Date(dateIso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function isDueAtPast(due_at: string | null | undefined, now = new Date()): boolean {
  if (!due_at) return false;
  return new Date(due_at).getTime() < now.getTime();
}

function toLocalDateISO(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
}

/** Tarefa pendente com prazo já vencido (data ou horário no dia atual). */
export function isTaskOverduePending(
  task: TaskUrgencyInput,
  timezone = "America/Sao_Paulo",
  now = new Date(),
): boolean {
  if (task.status !== "pending" || !task.due_date) return false;
  const today = todayInWorkspaceTz(timezone);
  return (
    task.due_date < today ||
    (task.due_date === today && isDueAtPast(task.due_at, now))
  );
}

export interface TaskCompletedLateInput {
  status: "pending" | "completed" | "cancelled";
  due_date: string | null;
  due_at?: string | null;
  completed_at?: string | null;
  updated_at?: string;
}

/** Tarefa concluída após o prazo (data ou horário no mesmo dia). */
export function isTaskCompletedLate(
  task: TaskCompletedLateInput,
  timezone = "America/Sao_Paulo",
): boolean {
  if (task.status !== "completed" || !task.due_date) return false;

  const completedInstant = task.completed_at ?? task.updated_at;
  if (!completedInstant) return false;

  const completedLocal = toLocalDateISO(completedInstant, timezone);
  if (completedLocal > task.due_date) return true;

  return (
    completedLocal === task.due_date &&
    !!task.due_at &&
    new Date(completedInstant).getTime() > new Date(task.due_at).getTime()
  );
}

export function getTaskUrgency(
  task: TaskUrgencyInput,
  timezone = "America/Sao_Paulo",
  now = new Date(),
): TaskUrgencyResult {
  if (task.status === "cancelled") {
    return {
      level: "cancelled",
      label: null,
      cardClassName: "",
      badgeVariant: null,
    };
  }
  if (task.status === "completed") {
    return {
      level: "completed",
      label: null,
      cardClassName: "",
      badgeVariant: null,
    };
  }

  if (!task.due_date) {
    return {
      level: "undated",
      label: "Sem prazo",
      cardClassName: "",
      badgeVariant: "outline",
      badgeClassName: "text-muted-foreground",
    };
  }

  const today = todayInWorkspaceTz(timezone);
  const tomorrow = addDaysISO(today, 1);
  const approachingEnd = addDaysISO(today, 3);

  if (isTaskOverduePending(task, timezone, now)) {
    return {
      level: "overdue",
      label: "Atrasada",
      cardClassName: "border-l-4 border-destructive",
      badgeVariant: "destructive",
    };
  }

  if (task.due_date === today) {
    return {
      level: "today",
      label: "Hoje",
      cardClassName: "border-l-4 border-amber-500",
      badgeVariant: "outline",
      badgeClassName: "bg-amber-500/15 text-amber-800 border-amber-500/40 dark:text-amber-300",
    };
  }

  if (task.due_date >= tomorrow && task.due_date <= approachingEnd) {
    return {
      level: "approaching",
      label: "Em breve",
      cardClassName: "border-l-4 border-yellow-500/70",
      badgeVariant: "outline",
      badgeClassName: "bg-yellow-500/10 text-yellow-800 border-yellow-500/30 dark:text-yellow-300",
    };
  }

  return {
    level: "future",
    label: null,
    cardClassName: "",
    badgeVariant: null,
  };
}
